// Libraries
import { CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Core
import { ResourceTextApplication } from '../../../core/resources';
import FormValidator from '../../../core/FormValidator';

// Features
import {
    ADialog,
    ADialogActions,
    ADialogContent,
    ADialogTitle,
} from '../../../common/dialog';
import { IContractListItem } from '../../../interfaces/IContract';
import {
    ADateInput,
    AInputItem,
    ARadioButtonGroupInputValue,
    ARadioButtons,
    ASwitch,
} from '../../../common/inputs';

export interface Context {
    context: string;
    displayName: string;
    scopes: string[];
}

const SOURCE_TZ = 'Etc/GMT+3'; //
const TARGET_TZ = 'Europe/Helsinki';

const toHkiDate = (d: Date | string | null | undefined): Date | null => {
    if (!d) return null;
    const m = moment(d);
    if (!m.isValid()) return null;
    return moment.tz(m.format('YYYY-MM-DDTHH:mm:ss'), SOURCE_TZ).tz(TARGET_TZ).toDate();
};

const fromHkiToSource = (d: Date | null): Date | null => {
    if (!d) return null;
    const m = moment(d);
    if (!m.isValid()) return null;
    return moment.tz(m.format('YYYY-MM-DDTHH:mm:ss'), TARGET_TZ).tz(SOURCE_TZ).toDate();
};

export interface ITerminateContractDialogProps {
    pdfUrl: string;
    title: string;
    contract: IContractListItem;
    terminateContract: (terminationDate: Date, terminatedBySnellman: boolean) => Promise<void>;
    onClose: (confirmed: boolean) => void;
}

export default function TerminateContractDialog(props: ITerminateContractDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>(['AnelmaGeneral', 'AnelmaContract']);
    const { contract } = props;
    const fakeValidator = new FormValidator(); // validate nothing, but required

    const [terminate, setTerminate] = useState<boolean>(false);
    const [terminationDate, setTerminationDate] = useState<Date | null>(new Date());
    const [terminator, setTerminator] = useState<'snellman' | 'producer'>('snellman');

    const title = props.title + ' ' + (props.contract.Signatories[0].ContractId ?? '');

    const terminateContract = () => {
        if (terminate && terminationDate) {
            const payloadDate = fromHkiToSource(terminationDate) ?? terminationDate;

            props
                .terminateContract(payloadDate, terminator === 'snellman')
                .then(() => props.onClose(true));
        }
    };

    const getItems: AInputItem<ARadioButtonGroupInputValue>[] = [
        {
            text: t('AnelmaContract:1044'),
            value: 'snellman',
        },
        {
            text: t('AnelmaContract:1003'),
            value: 'producer',
        },
    ];

    return !contract ? (
        <CircularProgress />
    ) : (
        <>
            <ADialog onClose={() => {}} open={true}>
                <ADialogContent size='lg'>
                    <ADialogTitle>{title}</ADialogTitle>

                    <div>
                        <iframe
                            id={'pdf-iframe'}
                            src={props.pdfUrl}
                            width='100%'
                            height='600px'
                            style={{ border: 'none' }}
                        ></iframe>
                    </div>

                    <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                        <Typography variant='h5'>{t('AnelmaContract:1034')}</Typography>
                    </Grid>
                    <Grid container>
                        <Grid container style={{ display: 'flex', flexFlow: 'row' }}>
                            <Grid size={{ sm: 6 }} style={{ textAlign: 'center' }}>
                                <Grid container>
                                    <Grid
                                        size={{ sm: 8 }}
                                        style={{
                                            textAlign: 'right',
                                            alignContent: 'center',
                                            height: '56px',
                                        }}
                                    >
                                        <Typography variant='body1'>
                                            {t('AnelmaContract:1035')}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        size={{ sm: 4 }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '56px',
                                        }}
                                    >
                                        <ASwitch
                                            id='terminate-switch'
                                            checked={terminate}
                                            onChange={() => setTerminate(!terminate)}
                                            withoutContainer
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{ sm: 6 }}>
                                <ADateInput
                                    id='termination-date'
                                    label={t('AnelmaContract:1036')}
                                    onChange={(v) => setTerminationDate(v)}
                                    disabled={!terminate}
                                    value={terminationDate}
                                    validator={fakeValidator}
                                    minDate={toHkiDate(contract.EffectiveFrom) ?? undefined}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        style={{
                            display: 'flex',
                            flexFlow: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Grid style={{ paddingLeft: '24px', paddingBottom: '16px' }}>
                            <Typography variant='h5'>{t('AnelmaContract:1013')}</Typography>
                        </Grid>

                        <Grid
                            size={{ sm: 8 }}
                            style={{ height: '56px', marginBottom: '30px', paddingLeft: '35px' }}
                        >
                            <ARadioButtons
                                id='terminator-toggle'
                                items={getItems}
                                onChange={(e) =>
                                    setTerminator(
                                        e?.toString() === 'producer' ? 'producer' : 'snellman'
                                    )
                                }
                                validator={new FormValidator()}
                                value={'snellman'}
                                withoutContainer
                            />
                        </Grid>
                    </Grid>

                    <ADialogActions
                        buttons={{
                            left: [
                                {
                                    onClick: () => props.onClose(false),
                                    type: 'cancel',
                                },
                            ],
                            right: [
                                {
                                    onClick: () => {
                                        terminateContract();
                                    },
                                    type: 'save',
                                    label: t('AnelmaContract:1035'),
                                    disabled: !terminate,
                                },
                            ],
                        }}
                    ></ADialogActions>
                </ADialogContent>
            </ADialog>
        </>
    );
}
